import React from "react";
import LegalPage from "../components/LegalPage";

const sections = [
  {
    id: "overview",
    heading: "Overview",
    body: (
      <>
        <p>
          MeinHaus Inc. (&ldquo;<strong>MeinHaus</strong>&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is committed
          to protecting your privacy. This Privacy Policy explains what personal information we collect when you
          use meinhaus.ca and our renovation-management platform (the &ldquo;Service&rdquo;), why we collect it,
          and the choices you have.
        </p>
        <p>
          By using the Service, you agree to the collection and use of information in accordance with this Policy.
          We comply with applicable Canadian privacy law, including the federal{" "}
          <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and provincial
          equivalents.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    heading: "Information we collect",
    body: (
      <>
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>
            <strong>Identification &amp; contact information</strong> — name, email, phone number, postal code,
            and project address.
          </li>
          <li>
            <strong>Project information</strong> — photos, descriptions, budgets, scope, and milestone updates
            you submit to your MeinHaus dashboard.
          </li>
          <li>
            <strong>Payment information</strong> — billing details and payment instrument metadata. Full card
            numbers are processed and stored by our PCI-DSS-compliant payment processor; MeinHaus does not store
            full card numbers.
          </li>
          <li>
            <strong>Usage &amp; device data</strong> — IP address, browser type, pages visited, referring URLs,
            and time stamps, collected through cookies and similar technologies.
          </li>
          <li>
            <strong>Communications</strong> — messages exchanged with contractors and MeinHaus support staff.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    heading: "How we use your information",
    body: (
      <>
        <p>We use your information to:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>match you with vetted local contractors and facilitate quotes;</li>
          <li>process milestone-based payments and refunds;</li>
          <li>fulfil our obligations under the Done-Right Guarantee;</li>
          <li>send service-related notifications (quotes, milestone approvals, schedule changes);</li>
          <li>improve, secure, and audit the Service;</li>
          <li>comply with legal, accounting, and regulatory obligations.</li>
        </ul>
        <p>
          We do not sell or rent your personal information. We do not use your information for targeted
          third-party advertising.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    heading: "Who we share information with",
    body: (
      <>
        <p>
          We share information only with parties necessary to deliver the Service:
        </p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>
            <strong>Contractors</strong> matched to your project receive the project scope, address, and your
            contact information required to perform the work.
          </li>
          <li>
            <strong>Service providers</strong> — payment processors, cloud hosting, analytics, email delivery,
            and customer-support tools, each bound by data-protection agreements.
          </li>
          <li>
            <strong>Legal compliance</strong> — where required by law, court order, or to protect the rights,
            property, or safety of MeinHaus, our users, or the public.
          </li>
          <li>
            <strong>Business transfers</strong> — in connection with a merger, acquisition, or sale of assets,
            subject to confidentiality and continued protection.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    heading: "Retention",
    body: (
      <p>
        We retain personal information for as long as needed to provide the Service, comply with our legal and
        tax obligations, resolve disputes, and enforce our agreements. Project records (including photos and
        completion sign-offs) are typically retained for at least seven years to support warranty claims and the
        Done-Right Guarantee.
      </p>
    ),
  },
  {
    id: "security",
    heading: "Security",
    body: (
      <p>
        We use industry-standard administrative, technical, and physical safeguards to protect your information,
        including TLS encryption in transit, encryption at rest for sensitive data, access controls based on
        least privilege, and periodic security reviews. No system is impenetrable; please notify us promptly at{" "}
        <a
          href="mailto:security@meinhaus.ca"
          className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
        >
          security@meinhaus.ca
        </a>{" "}
        if you suspect a security issue.
      </p>
    ),
  },
  {
    id: "your-rights",
    heading: "Your rights",
    body: (
      <>
        <p>You have the right to:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>access and review the personal information we hold about you;</li>
          <li>request correction of inaccurate information;</li>
          <li>request deletion of your account and associated personal data, subject to retention exceptions;</li>
          <li>withdraw consent for marketing communications at any time;</li>
          <li>lodge a complaint with the Office of the Privacy Commissioner of Canada or your provincial regulator.</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a
            href="mailto:privacy@meinhaus.ca"
            className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
          >
            privacy@meinhaus.ca
          </a>
          . We will respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "Cookies",
    body: (
      <p>
        We use first-party cookies and similar technologies to remember your preferences, keep you signed in,
        and measure how the Service is used. You can disable cookies in your browser settings, but some parts of
        the Service may not function correctly without them.
      </p>
    ),
  },
  {
    id: "children",
    heading: "Children",
    body: (
      <p>
        The Service is not directed to individuals under 18 and we do not knowingly collect personal information
        from anyone under 18. If you believe we have collected such information, please contact us and we will
        delete it.
      </p>
    ),
  },
  {
    id: "changes",
    heading: "Changes to this Policy",
    body: (
      <p>
        We may update this Policy from time to time. When we do, we&rsquo;ll change the &ldquo;Last
        updated&rdquo; date at the top and, for material changes, notify you by email or in the dashboard. Your
        continued use of the Service after the changes take effect constitutes acceptance.
      </p>
    ),
  },
  {
    id: "contact",
    heading: "Contact us",
    body: (
      <p>
        MeinHaus Inc., 100 King Street West, Toronto, ON, Canada.
        <br />
        Privacy questions:{" "}
        <a
          href="mailto:privacy@meinhaus.ca"
          className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
        >
          privacy@meinhaus.ca
        </a>
        .
      </p>
    ),
  },
];

const PrivacyPolicy = () => (
  <LegalPage
    eyebrow="Privacy"
    title="Privacy Policy"
    lede="What we collect, why we collect it, and the choices you have. Plain language. No dark patterns."
    updated="February 8, 2026"
    sections={sections}
  />
);

export default PrivacyPolicy;
